import React, {useCallback, useEffect, useRef, useState} from "react";
import {
    Row,
    Form,
    Col,
    Tabs,
    Tab,
    Pagination,
    Table,
    Button,
    Popover,
    Spinner,
    OverlayTrigger,
    Tooltip,
} from "react-bootstrap";
import Accordion from "react-bootstrap/Accordion";
import {ColumnDef, flexRender, getCoreRowModel, SortingState, useReactTable} from "@tanstack/react-table";
import {Utils} from "../../common/utils";
import {
    EnfluxQuestion,
    QuestionCategory,
    useQuestionsCategories,
    useEnfluxQuestions,
    enfluxQuestionsKey,
    CompetencyInfo,
} from "@/hooks/categorizer";
import DebouncedInput from "Components/Common/DebouncedInput";
import {ThumbsUpIcon} from "Components/Icons/ThumbsUpIcon";
import {ThumbsDownIcon} from "Components/Icons/ThumbsDownIcon";
import {SortAscendingIcon} from "Components/Icons/SortAscending";
import {SortDescendingIcon} from "Components/Icons/SortDescending";
import {EAIClient} from "Services/EAIClient";
import {createPortal} from "react-dom";
import {SelectWrapper} from "Components/Common/SelectWrapper";
import { mutate } from "swr";
import ArrowDownIcon from "Components/Icons/ArrowDownIcon";
import ArrowUpIcon from "Components/Icons/ArrowUpIcon";
import {useAppContext} from "@/contexts/app";
import {InfoCircleIcon} from "Components/Icons/Icons";
import PageTitle from "Components/Common/PageTitle";

type FormState = {
    itemId: string;
    courseName: string;
    assessment: string;
    itemCreator: string;
    reason: string;
    competency: string;
    subCompetency: string;
}

export default function CompetencyGeniePage() {
    const [formState, setFormState] = useState<FormState>({
        itemId: "",
        courseName: "",
        assessment: "",
        itemCreator: "",
        reason: "",
        competency: "",
        subCompetency: ""
    });

    useEffect(() => {
        Utils.setViewName("CompetencyGenie", "CompetencyGenie");
    }, []);

    const handleInputChange = useCallback((field: keyof FormState, value: string) => {
        setFormState(prevState => ({ ...prevState, [field]: value }));
    }, []);

    return (
        <div className="m-3 border border-1 rounded bg-gray p-3">
            <PageTitle pageTitle="CompetencyGenie" />
            <Row className="mb-4 align-items-center">
                <Col>
                    <img src="https://enflux-static-prod.s3.us-east-1.amazonaws.com/cg_logo.png" alt="CompetencyGenie"/>
                </Col>
                <Col className="text-end">
                    <OverlayTrigger overlay={<Tooltip className="dashboard-card-tooltip">Learn more in the Knowledge Base Article</Tooltip>}>
                        <Button variant="purple-link" onClick={() => window.open("https://knowledgebase.enflux.com/en/competencygenie-ui-tool", "_blank")}>
                            <InfoCircleIcon className="me-1" fill={"#973794"} />
                            Learn more
                        </Button>
                    </OverlayTrigger>

                </Col>
            </Row>
            <div className="rounded bg-light-gray p-2 mb-3">
                <Row className="mb-3">
                    <InputField label="Item ID" value={formState.itemId} placeholder="Item ID" onChange={value => handleInputChange("itemId", value)} type="number" />
                    <InputField label="Course Name" value={formState.courseName} placeholder="Course Name" onChange={value => handleInputChange("courseName", value)} />
                    <InputField label="Assessment Name" value={formState.assessment} placeholder="Assessment" onChange={value => handleInputChange("assessment", value)} />
                    <InputField label="Item Creator" value={formState.itemCreator} placeholder="Item Creator" onChange={value => handleInputChange("itemCreator", value)} />
                    <InputField label="Reason" value={formState.reason} placeholder="Reason" onChange={value => handleInputChange("reason", value)} />
                </Row>
                <Accordion>
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>Advanced Filters</Accordion.Header>
                        <Accordion.Body>
                            <Form as={Row}>
                                <InputField label="Competency" value={formState.competency} placeholder="Competency" onChange={value => handleInputChange("competency", value)} />
                                <InputField label="Subcompetency" value={formState.subCompetency} placeholder="Subcompetency" onChange={value => handleInputChange("subCompetency", value)} />
                            </Form>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </div>
            <Tabs fill>
                <Tab title="Needs Feedback" eventKey="needs_feedback">
                    <NeedsFeedbackTable {...formState} />
                </Tab>
                <Tab title="Verified Questions" eventKey="verified_questions">
                    <VerifiedQuestionsTable {...formState} />
                </Tab>
            </Tabs>
        </div>
    );
}

type NeedsFeedbackTableProps = {
    courseName: string
    assessment: string
    itemId: string
    itemCreator: string
    reason: string
    competency: string
    subCompetency: string
}

function NeedsFeedbackTable(
    {
        courseName,
        assessment,
        itemId,
        itemCreator,
        reason,
        competency,
        subCompetency}: NeedsFeedbackTableProps) {
    const [pageSize, setPageSize] = useState("10");
    const [pageNumber, setPageNumber] = useState("1");
    const [sorting, setSorting] = useState<SortingState>([]);
    const [sortBy, setSortBy] = useState("");
    const [sortOrder, setSortOrder] = useState("");
    const {error: errorUnverifiedQuestions, data: unverifiedQuestions} = useEnfluxQuestions(
        {
            verified: false,
            courseName,
            assessmentName: assessment,
            itemId,
            itemCreator,
            reason,
            competency,
            subCompetency,
            pageSize,
            pageNumber,
            sortBy,
            sortOrder,
        });
    const columns: ColumnDef<EnfluxQuestion>[] = [
        {header: "Data Source", accessorKey: "datasource", size: 8},
        {header: "Item ID", accessorKey: "item_id", size: 6},
        {
            header: "Question",
            accessorKey: "question",
            size: 29,
            cell: (cell) => (
                <ViewMoreContent content={cell.getValue() as string} />
            )
        },
        {
            header: "Competency",
            accessorKey: "competency",
            size: 12,
            cell: (cell) => {
                const rows: CompetencyInfo[] = cell.row.original.competency_info;
                return (
                    <table className="w-100">
                        <tbody>
                        {rows.map((info, index) => (
                            <tr key={info.ruuid} style={{
                                display: "inline-block",
                                borderBottom: index < rows.length - 1 ? "1px solid #ddd" : "",
                                width: "100%"}}>
                                    <td key={index}
                                        style={{
                                            display: "inline-block",
                                            width: "100%",
                                            minHeight: "30px",
                                        }}>
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                <Tooltip className="dashboard-card-tooltip">
                                                    {info.competency}
                                                </Tooltip>
                                            }
                                        >
                                            <div className="truncate-1-line">{info.competency}</div>
                                        </OverlayTrigger>
                                    </td>
                            </tr>))}
                        </tbody>
                    </table>
                );
            }
        },
        {
            header: "Subcompetency",
            accessorKey: "subcompetency",
            size: 14,
            cell: (cell) => {
                const rows: CompetencyInfo[] = cell.row.original.competency_info;
                return (
                    <table className="w-100">
                        <tbody>
                        {rows.map((info, index) => (
                            <tr key={info.ruuid} style={{
                                display: "inline-block",
                                borderBottom: index < rows.length - 1 ? "1px solid #ddd" : "",
                                width: "100%"}}>
                            <td
                                style={{
                                    display: "inline-block",
                                    width: "100%",
                                    minHeight: "30px",
                                }}>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip className="dashboard-card-tooltip">
                                            {info.subcompetency}
                                        </Tooltip>
                                    }
                                >
                                    <div className="truncate-1-line">{info.subcompetency}</div>
                                </OverlayTrigger>
                            </td>
                        </tr>))}
                        </tbody>
                    </table>
                );
            },
        },
        {
            header: "Reason",
            accessorKey: "reason",
            size: 10,
            cell: (cell) => {
                const rows: CompetencyInfo[] = cell.row.original.competency_info;
                return (
                    <table className="w-100">
                        <tbody>
                        {rows.map((info, index) => (
                            <tr key={info.ruuid} style={{
                                display: "inline-block",
                                borderBottom: index < rows.length - 1 ? "1px solid #ddd" : "",
                                width: "100%"}}>
                            <td key={index}
                                style={{
                                    display: "inline-block",
                                    width: "100%",
                                    minHeight: "30px",
                                }}>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip className="dashboard-card-tooltip">
                                            {info.reason}
                                        </Tooltip>
                                    }
                                >
                                    <div className="truncate-1-line">{info.reason}</div>
                                </OverlayTrigger>
                            </td>
                        </tr>))}
                        </tbody>
                    </table>
                );
            },
        },
        {
            header: "Feedback",
            id: "feedback",
            enableSorting: false,
            size: 6,
            cell: (cell) => {
                const rows: CompetencyInfo[] = cell.row.original.competency_info;
                return (
                    <table className="w-100">
                        <tbody>
                        {rows.map((info, index) => (
                            <tr key={index} style={{
                                display: "inline-block",
                                width: "100%",
                                borderBottom: index < rows.length - 1 ? "1px solid #ddd" : "",
                                minHeight: "30px"}}>
                                <td
                                    style={{
                                        display: "inline-block",
                                        width: "100%",
                                        minHeight: "30px",
                                    }}>
                                    <ThumbsUpButton
                                        info={info}
                                        onSubmit={() => {
                                            mutate((key) => typeof key === "string" && key.startsWith(enfluxQuestionsKey), undefined, {revalidate: true});
                                        }}/>
                                    <FocusedPopoverForm
                                        onSubmit={() => {
                                            mutate((key) => typeof key === "string" && key.startsWith(enfluxQuestionsKey), undefined, {revalidate: true});
                                        }}
                                        info={info}/>
                                </td>
                        </tr>))}
                        </tbody>
                    </table>
                );
            }
        }];
    const table = useReactTable({
        columns,
        data: unverifiedQuestions?.data ?? [],
        state: {sorting},
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        manualPagination: true,
        manualFiltering: true,
        manualSorting: true,
        defaultColumn: {
            size: 200,
            minSize: 0,
            maxSize: 500,
        },
    });

    const pagesCount = () => {
        return Math.ceil((unverifiedQuestions?.result.total_count ?? 0) / parseInt(pageSize));
    };

    useEffect(() => {
        if (sorting.length > 0) {
            const sort = sorting[0];
            setSortBy(sort.id);
            setSortOrder(sort.desc ? "desc" : "asc");
        } else {
            setSortBy("");
            setSortOrder("");
        }
    }, [sorting]);

    const renderPaginate = () => {
        const pageNumbers: number[] = [];
        const totalCount = unverifiedQuestions?.result.total_count ?? 0;
        const perPage = parseInt(pageSize);
        const curPage = parseInt(pageNumber);
        const totalPages = Math.ceil(totalCount / perPage);
        // calculate the page numbers with the current page in the middle, the page numbers will not always start a 1
        // and the page numbers will have a maximum of 5 pages
        for (let i = Math.max(1, curPage-2); i <= Math.min(totalPages, curPage+2); i++) {
            if (i < totalPages) {
                pageNumbers.push(i);
            }
        }

        // need to fill the page numbers if the length is less than 5
        if (pageNumbers.length < 5 && totalCount > 1) {
            const diff = 5 - pageNumbers.length;
            for (let i = 1; i <= diff; i++) {
                if (pageNumbers[0] - i > 1) {
                    pageNumbers.unshift(pageNumbers[0] - i);
                } else {
                    const newPage = pageNumbers[pageNumbers.length - 1] + 1;
                    if (newPage <= totalPages){
                        pageNumbers.push(newPage);
                    }
                }
            }
        }
        if (pageNumbers.length === 0) {
            pageNumbers.push(1);
        }

        return pageNumbers.map((number) => (
            <Pagination.Item
                key={number}
                active={number === Number(pageNumber)}
                onClick={() => setPageNumber(number.toString())}>{number}</Pagination.Item>));
    };

    const renderBody = () => {
        if (errorUnverifiedQuestions) {
            return (
                <tr>
                    <td colSpan={columns.length} className="text-center text-danger">{errorUnverifiedQuestions.message}</td>
                </tr>
            );
        }

        if (!unverifiedQuestions) {
            return (
                <tr>
                    <td colSpan={columns.length} className="text-center"><Spinner animation="border" size="sm" /></td>
                </tr>
            );
        }

        if (table.getRowModel().rows.length === 0) {
            return (
                <tr>
                    <td colSpan={columns.length} className="text-center">No questions available</td>
                </tr>
            );
        }

        return table.getRowModel().rows.map(row => (
            <tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                    <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                ))}
            </tr>
        ));
    };

    return (
        <>
            <Table className="mt-2 styled-table" responsive={true}>
                <thead>
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                            <th
                                key={header.id}
                                onClick={header.column.getToggleSortingHandler()}
                                style={{ width: `${header.getSize()}%`, ...(header.id === "feedback" ? {minWidth: "100px"} : {}) }}>
                                <div className={`table_${header.index} header-div`}>
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                        )}
                                    {{
                                        asc: <SortAscendingIcon className="header-icon"/>,
                                        desc: <SortDescendingIcon className="header-icon"/>,
                                    }[header.column.getIsSorted() as string] ?? null}
                                </div>
                            </th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody>
                {renderBody()}
                </tbody>
            </Table>
            <Row className="mt-3">
                <Col sm={3}>
                    <span className="fw-gray font-small">Rows per page:</span>{" "}
                    <Form.Select
                        style={{width: "70px", display: "inline-block"}}
                        onChange={(ev) => {
                            setPageSize(ev.target.value);
                            setPageNumber("1");
                        }} value={pageSize}>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                    </Form.Select>
                </Col>
                <Col className="justify-content-end d-flex align-items-center" sm={9} >
                    <Pagination className="mb-0 me-1">
                        <Pagination.First onClick={() => setPageNumber("1")} />
                        <Pagination.Prev onClick={() => {
                            const newPage = parseInt(pageNumber) - 1;
                            if (newPage > 0) {
                                setPageNumber(newPage.toString());
                            } else {
                                setPageNumber("1");
                            }
                        }} />
                        {renderPaginate()}
                        <Pagination.Next onClick={() => {
                            const newPage = parseInt(pageNumber) + 1;
                            if (newPage <= pagesCount()) {
                                setPageNumber(newPage.toString());
                            } else {
                                setPageNumber(pagesCount().toString());
                            }
                        }} />
                        <Pagination.Last onClick={() => setPageNumber(pagesCount().toString())} />
                    </Pagination>
                    of {pagesCount()} pages
                </Col>
            </Row>
        </>
    );
}

type VerifiedQuestionsTableProps = {
    courseName: string
    assessment: string
    itemId: string
    itemCreator: string
    reason: string
    competency: string
    subCompetency: string
}

function VerifiedQuestionsTable(
    {
        courseName,
        assessment,
        itemId,
        itemCreator,
        reason,
        competency,
        subCompetency
    }: VerifiedQuestionsTableProps) {
    const [pageSize, setPageSize] = useState("10");
    const [pageNumber, setPageNumber] = useState("1");
    const [sorting, setSorting] = useState<SortingState>([]);
    const [sortBy, setSortBy] = useState("");
    const [sortOrder, setSortOrder] = useState("");
    const {error: errorVerifiedQuestions, data: verifiedQuestions} = useEnfluxQuestions(
        {
            courseName,
            assessmentName: assessment,
            itemId,
            itemCreator,
            reason,
            competency,
            subCompetency,
            pageSize,
            pageNumber,
            sortBy,
            sortOrder,
            verified: true
        }
    );

    const columns: ColumnDef<EnfluxQuestion>[] = [
        {header: "Data Source", accessorKey: "datasource", size: 8},
        {header: "Item ID", accessorKey: "item_id", size: 6},
        {
            header: "Question",
            accessorKey: "question",
            size: 29,
            cell: (cell) => (
                <ViewMoreContent content={cell.getValue() as string} />
            )
        },
        {
            header: "Competency",
            accessorKey: "competency",
            size: 12,
            cell: (cell) => {
                const rows = cell.row.original.competency_info;
                return (
                    <table className="w-100">
                        <tbody>
                        {rows.map((info, index) => (
                            <tr key={info.ruuid} style={{
                                display: "inline-block",
                                borderBottom: index < rows.length - 1 ? "1px solid #ddd" : "",
                                width: "100%"}}>
                                <td key={index}
                                    style={{
                                        display: "inline-block",
                                        width: "100%",
                                        minHeight: "30px",
                                    }}>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip className="dashboard-card-tooltip">
                                                {info.competency}
                                            </Tooltip>
                                        }
                                    >
                                        <div className="truncate-1-line">{info.competency}</div>
                                    </OverlayTrigger>
                                </td>
                            </tr>))}
                        </tbody>
                    </table>
                );
            }
        },
        {
            header: "Subcompetency",
            accessorKey: "subcompetency",
            size: 14,
            cell: (cell) => {
                const rows: CompetencyInfo[] = cell.row.original.competency_info;
                return (
                    <table className="w-100">
                        <tbody>
                        {rows.map((info, index) => (
                            <tr key={info.ruuid} style={{
                                display: "inline-block",
                                borderBottom: index < rows.length - 1 ? "1px solid #ddd" : "",
                                width: "100%"}}>
                            <td
                                style={{
                                    display: "inline-block",
                                    width: "100%",
                                    minHeight: "30px",
                                }}>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip className="dashboard-card-tooltip">
                                            {info.subcompetency}
                                        </Tooltip>
                                    }
                                >
                                    <div className="truncate-1-line">{info.subcompetency}</div>
                                </OverlayTrigger>
                            </td>
                        </tr>))}
                        </tbody>
                    </table>
                );
            },
        },
        {
            header: "Reason",
            accessorKey: "reason",
            size: 10,
            cell: (cell) => {
                const rows: CompetencyInfo[] = cell.row.original.competency_info;
                return (
                    <table className="w-100">
                        <tbody>
                        {rows.map((info, index) => (
                            <tr key={info.ruuid} style={{
                                display: "inline-block",
                                borderBottom: index < rows.length - 1 ? "1px solid #ddd" : "",
                                width: "100%"}}>
                                <td key={index}
                                    style={{
                                        display: "inline-block",
                                        width: "100%",
                                        minHeight: "30px",
                                    }}>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip className="dashboard-card-tooltip">
                                                {info.reason}
                                            </Tooltip>
                                        }
                                    >
                                        <div className="truncate-1-line">{info.reason}</div>
                                    </OverlayTrigger>
                                </td>
                            </tr>))}
                        </tbody>
                    </table>
                );
            },
        },
        {
            header: "Feedback",
            id: "feedback",
            enableSorting: false,
            size: 6,
            cell: (cell) => {
                const rows: CompetencyInfo[] = cell.row.original.competency_info;
                return (
                    <table className="w-100">
                        <tbody>
                        {rows.map((info, index) => (
                            <tr key={index} style={{
                                display: "inline-block",
                                width: "100%",
                                borderBottom: index < rows.length - 1 ? "1px solid #ddd" : "",
                                minHeight: "30px"}}>
                                <td
                                    style={{
                                        display: "inline-block",
                                        width: "100%",
                                        minHeight: "30px",
                                    }}>
                                    <Button variant="icon" className="pt-0 pb-0">
                                        <ThumbsUpIcon variant={info.reported_verified ? "primary" : "info"}/>
                                    </Button>
                                    <Button variant="icon" className="pt-0 pb-0">
                                        <ThumbsDownIcon variant={info.reported_verified ? "info" : "primary"}/>
                                    </Button>
                                </td>
                            </tr>))}
                        </tbody>
                    </table>
                );
            }
        }
    ];
    const table = useReactTable({
        columns,
        data: verifiedQuestions?.data ?? [],
        state: {sorting},
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        manualPagination: true,
        manualFiltering: true,
        manualSorting: true,
        defaultColumn: {
            size: 200,
            minSize: 0,
            maxSize: 500,
        },
    });

    const pagesCount = () => {
        return Math.ceil((verifiedQuestions?.result.total_count ?? 0) / parseInt(pageSize));
    };

    useEffect(() => {
        if (sorting.length > 0) {
            const sort = sorting[0];
            setSortBy(sort.id);
            setSortOrder(sort.desc ? "desc" : "asc");
        } else {
            setSortBy("");
            setSortOrder("");
        }
    }, [sorting]);

    const renderPaginate = () => {
        const pageNumbers: number[] = [];
        const totalCount = verifiedQuestions?.result.total_count ?? 0;
        const perPage = parseInt(pageSize);
        const curPage = parseInt(pageNumber);
        const totalPages = Math.ceil(totalCount / perPage);
        // calculate the page numbers with the current page in the middle, the page numbers will not always start a 1
        // and the page numbers will have a maximum of 5 pages
        for (let i = Math.max(1, curPage-2); i <= Math.min(totalPages, curPage+2); i++) {
            if (i < totalPages) {
                pageNumbers.push(i);
            }
        }

        // need to fill the page numbers if the length is less than 5
        if (pageNumbers.length < 5 && totalCount > 1) {
            const diff = 5 - pageNumbers.length;
            for (let i = 1; i <= diff; i++) {
                if (pageNumbers[0] - i > 1) {
                    pageNumbers.unshift(pageNumbers[0] - i);
                } else {
                    const newPage = pageNumbers[pageNumbers.length - 1] + 1;
                    if (newPage <= totalPages){
                        pageNumbers.push(newPage);
                    }
                }
            }
        }
        if (pageNumbers.length === 0) {
            pageNumbers.push(1);
        }

        return pageNumbers.map((number) => (
            <Pagination.Item
                key={number}
                active={number === Number(pageNumber)}
                onClick={() => setPageNumber(number.toString())}>{number}</Pagination.Item>));
    };

    const renderBody = () => {
        if (errorVerifiedQuestions) {
            return (
                <tr>
                    <td colSpan={columns.length} className="text-center text-danger">{errorVerifiedQuestions.message}</td>
                </tr>
            );
        }

        if (!verifiedQuestions) {
            return (
                <tr>
                    <td colSpan={columns.length} className="text-center"><Spinner animation="border" size="sm" /></td>
                </tr>
            );
        }

        if (table.getRowModel().rows.length === 0) {
            return (
                <tr>
                    <td colSpan={columns.length} className="text-center">No questions available</td>
                </tr>
            );
        }

        return table.getRowModel().rows.map(row => (
            <tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                    <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                ))}
            </tr>
        ));
    };

    return (
        <>
            <Table className="mt-2 styled-table" responsive={true}>
                <thead>
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                            <th
                                key={header.id}
                                onClick={header.column.getToggleSortingHandler()}
                                style={{ width: `${header.getSize()}%`, ...(header.id === "feedback" ? {minWidth: "102px"} : {}) }}>
                                <div className={`table_${header.index} header-div`}>
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                        )}
                                    {{
                                        asc: <SortAscendingIcon className="header-icon"/>,
                                        desc: <SortDescendingIcon className="header-icon"/>,
                                    }[header.column.getIsSorted() as string] ?? null}
                                </div>
                            </th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody>
                {renderBody()}
                </tbody>
            </Table>
            <Row className="mt-3">
                <Col sm={3}>
                    <span className="fw-gray font-small">Rows per page:</span>{" "}
                    <Form.Select
                        style={{width: "70px", display: "inline-block"}}
                        onChange={(ev) => {
                            setPageSize(ev.target.value);
                            setPageNumber("1");
                        }} value={pageSize}>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                    </Form.Select>
                </Col>
                <Col className="justify-content-end d-flex align-items-center" sm={9} >
                    <Pagination className="mb-0 me-1">
                        <Pagination.First onClick={() => setPageNumber("1")} />
                        <Pagination.Prev onClick={() => {
                            const newPage = parseInt(pageNumber) - 1;
                            if (newPage > 0) {
                                setPageNumber(newPage.toString());
                            } else {
                                setPageNumber("1");
                            }
                        }} />
                        {renderPaginate()}
                        <Pagination.Next onClick={() => {
                            const newPage = parseInt(pageNumber) + 1;
                            if (newPage <= pagesCount()) {
                                setPageNumber(newPage.toString());
                            } else {
                                setPageNumber(pagesCount().toString());
                            }
                        }} />
                        <Pagination.Last onClick={() => setPageNumber(pagesCount().toString())} />
                    </Pagination>
                    of {pagesCount()} pages
                </Col>
            </Row>
        </>
    );
}

function ThumbsUpButton({info, onSubmit}: {info: CompetencyInfo, onSubmit: () => void}) {
    const [loading, setLoading] = useState(false);
    const client = new EAIClient();
    const {dispatchError} = useAppContext();

    const handleSubmit = async () => {
        try {
            setLoading(true);
            await client.sendCategorizedQuestionFeedback(info.ruuid, true);
            onSubmit();
        } catch (e) {
            dispatchError(e);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Button variant="icon">
                <Spinner animation="border" size="sm" />
            </Button>
        );
    }

    return (
        <Button className="pt-0 pb-0" variant="icon" onClick={handleSubmit}>
            <ThumbsUpIcon variant="info" />
        </Button>
    );
}

function FocusedPopoverForm({onSubmit, info}: {onSubmit: () => void, info: CompetencyInfo}) {
    const {data: questionsCategoriesResponse} = useQuestionsCategories(info.competency);
    const [categories, setCategories] = useState<QuestionCategory[]>([]);
    const [show, setShow] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [category, setCategory] = useState<QuestionCategory|null>(null);
    const [comment, setComment] = useState("");
    const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
    const buttonRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const {dispatchError} = useAppContext();
    const client = new EAIClient();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setFetching(true);
            await client.sendCategorizedQuestionFeedback(info.ruuid, false, category?.subcompetency, comment, info.subcompetency);
            setShow(false);
            onSubmit();
        } catch (e) {
            dispatchError(e);
        } finally {
            setFetching(false);
        }
    };

    const isDisabledSubmit = () => !category || !comment;

    useEffect(() => {
        if (show && buttonRef.current && popoverRef.current) {
            const buttonRect = buttonRef.current.getBoundingClientRect();
            const popoverRect = popoverRef.current.getBoundingClientRect();
            setPopoverPosition({
                top: buttonRect.top + window.scrollY - popoverRect.height - 10, // 10px spacing
                left: buttonRect.left + window.scrollX - 400
            });
        }
    }, [show]);

    useEffect(() => {
        if (questionsCategoriesResponse) {
            const notApplicable: QuestionCategory = {
                competency: "Not Applicable",
                subcompetency: "Not Applicable",
                model_ruuid: "not_app",
            };
            const newCategories = [notApplicable, ...questionsCategoriesResponse.data];
            setCategories(newCategories);
        } else {
            setCategories([]);
        }
    }, [questionsCategoriesResponse]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target as Node) &&
                buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
                setShow(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const popover = show ? (
        <div
            ref={popoverRef}
            style={{
                position: "absolute",
                top: `${popoverPosition.top}px`,
                left: `${popoverPosition.left}px`,
                zIndex: 1060
            }}
        >
            <Popover id="popover-feedback" style={{maxWidth: "400px"}} arrowProps={{ style: {display: "none"} }}>
                <Popover.Body className="">
                    <Form onSubmit={handleSubmit}>
                        <Form.Group controlId="formSelect">
                            <Form.Label column="sm">1. Select the category in which you consider your question fits best.</Form.Label>
                            <SelectWrapper
                                getOptionLabel={option => option.subcompetency}
                                getOptionValue={option => option.model_ruuid}
                                isClearable={false}
                                placeholder="Select a Category"
                                value={category}
                                onChange={option => setCategory(option)}
                                options={categories} />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="formTextarea">
                            <Form.Label column="sm">2. How would you say your question fits better in this category?</Form.Label>
                            <Form.Control as="textarea" rows={3} placeholder="Enter your message here" onChange={ev => setComment(ev.target.value)} />
                        </Form.Group>
                        <div className="d-flex justify-content-end">
                            <Button variant="secondary" className="me-2" onClick={() => setShow(false)}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={fetching || isDisabledSubmit()}>
                                {fetching ? "Sending..." : "Send"}
                            </Button>
                        </div>
                    </Form>
                </Popover.Body>
            </Popover>
        </div>
    ) : null;

    const overlay = show ? (
        createPortal(
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 1050
                }}
            />,
            document.body
        )
    ) : null;

    return (
        <>
            <Button ref={buttonRef} className="pt-0 pb-0" variant="icon" onClick={() => setShow(!show)}>
                <ThumbsDownIcon variant="info" />
            </Button>
            {overlay}
            {popover}
        </>
    );
}

function ViewMoreContent({content}: {content: string}) {
    const [show, setShow] = useState(false);
    const showButton = content.length > 170;

    return (
        <div>
            <div className={show ? "" : "truncate-2-lines"}>
                {content}
            </div>
            {showButton && <Button
                variant="outline-info"
                className="bg-transparent border-0 d-flex align-items-center ps-0"
                style={{transition: "all 0.2s"}}
                onClick={() => setShow(!show)}
            >
                View more <span className="ms-1">{show ? <ArrowUpIcon/> : <ArrowDownIcon/>}</span>
            </Button>}
        </div>
    );
}

type InputFieldProps = {
    label: string;
    value: string;
    placeholder: string;
    onChange: (value: string) => void;
    type?: string;
}

function InputField({label, value, placeholder, onChange, type = "text"}: InputFieldProps) {
    return (
        <Form.Group as={Col}>
            <Form.Label column={false}>{label}</Form.Label>
            <DebouncedInput value={value} type={type} placeholder={placeholder} onChange={val => onChange(val.toString())} />
        </Form.Group>
    );
}
